<script setup lang="ts">
import { ref, watch } from 'vue';
import api from '~/api';

const props = defineProps<{
  tool: 'text-to-image' | 'image-to-image';
  /** Latest result from panel — sync providers have results, async have task_id */
  latestResult?: { order_id: string; task_id?: string; results: { url: string }[] } | null;
}>();
const { t } = useI18n();

interface GalleryItem {
  order_id: string;
  status: string;
  task_id: string | null;
  results: { url: string }[];
  error?: string;
  _polling?: boolean;
}

const items = ref<GalleryItem[]>([]);

function pollItem(item: GalleryItem) {
  if (item._polling) return;
  item._polling = true;
  let attempts = 0;
  const timer = setInterval(async () => {
    attempts++;
    try {
      const res = await api.fetchImageGenResult(item.order_id, item.task_id ?? undefined);
      if (res.task_status === 1) {
        item.status = 'COMPLETED';
        item.results = res.results || [];
        item._polling = false;
        clearInterval(timer);
      } else if (res.task_status === -1 || attempts >= 60) {
        item.status = 'FAILED';
        item.error = res.message;
        item._polling = false;
        clearInterval(timer);
      }
    } catch { /* ignore */ }
  }, 6000);
}

function deleteItem(orderId: string) {
  items.value = items.value.filter((i) => i.order_id !== orderId);
  api.deleteGeneratedImage(orderId).catch(() => {});
}

function downloadImage(url: string) {
  const a = document.createElement('a');
  a.href = `/api/download?url=${encodeURIComponent(url)}`;
  a.download = '';
  a.click();
}

// Watch for new results pushed from panel
watch(() => props.latestResult, (val) => {
  if (!val) return;
  const isCompleted = val.results.length > 0;
  const existing = items.value.find((i) => i.order_id === val.order_id);
  if (!existing) {
    const item: GalleryItem = {
      order_id: val.order_id,
      status: isCompleted ? 'COMPLETED' : 'PROCESSING',
      task_id: val.task_id || null,
      results: val.results,
    };
    items.value.unshift(item);
    // Async task: start polling — must use reactive proxy from array, not plain object
    if (!isCompleted && item.task_id) {
      pollItem(items.value[0]);
    }
  } else {
    if (isCompleted) {
      existing.status = 'COMPLETED';
      existing.results = val.results;
    }
  }
});

// 切换 tool 时清空当前会话结果
watch(() => props.tool, () => {
  items.value = [];
});
</script>

<template>
  <div class="gallery-panel">
    <h3 class="title">{{ t('imageGen.gallery') }}</h3>

    <div v-if="items.length === 0" class="empty">
      {{ t('imageGen.noImages') }}
    </div>

    <div v-else class="grid">
      <div v-for="item in items" :key="item.order_id" class="card">
        <!-- PROCESSING -->
        <template v-if="item.status === 'PROCESSING'">
          <div class="card-loading">
            <v-progress-circular indeterminate size="32" width="2" color="amber" />
            <span class="text-xs text-white/50 mt-2">{{ t('imageGen.processing') }}</span>
          </div>
        </template>

        <!-- COMPLETED -->
        <template v-else-if="item.status === 'COMPLETED'">
          <div v-for="(img, i) in item.results" :key="i" class="card-img-wrap">
            <img :src="img.url" class="card-img" loading="lazy" />
            <div class="card-overlay">
              <button class="icon-btn" @click="downloadImage(img.url)" :title="t('imageGen.download')">
                <v-icon icon="mdi-download" size="18" />
              </button>
              <button class="icon-btn danger" @click="deleteItem(item.order_id)" :title="t('imageGen.delete')">
                <v-icon icon="mdi-delete-outline" size="18" />
              </button>
            </div>
          </div>
        </template>

        <!-- FAILED -->
        <template v-else>
          <div class="card-failed">
            <v-icon icon="mdi-alert-circle-outline" size="24" color="red" />
            <span class="text-xs text-red-400 mt-1">{{ item.error || 'Failed' }}</span>
            <button class="text-xs text-white/40 mt-1 hover:text-white/60" @click="deleteItem(item.order_id)">
              {{ t('imageGen.delete') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.gallery-panel {
  @apply rounded-2xl bg-neutral-900/70 border border-white/5 p-4 md:p-6;
  min-height: 480px;
}
.title { @apply text-sm font-semibold text-white/70 mb-4; }
.empty {
  @apply flex flex-col items-center justify-center py-12 text-sm text-neutral-500;
}
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.card {
  @apply rounded-xl overflow-hidden bg-neutral-950/50 border border-neutral-800;
  aspect-ratio: 1;
}
.card-loading, .card-failed {
  @apply flex flex-col items-center justify-center w-full h-full;
}
.card-img-wrap {
  @apply relative w-full h-full;
}
.card-img {
  @apply w-full h-full object-cover;
}
.card-overlay {
  @apply absolute bottom-0 left-0 right-0 flex items-center justify-end gap-2 p-2
    bg-gradient-to-t from-black/60 to-transparent opacity-0 transition;
}
.card-img-wrap:hover .card-overlay { opacity: 1; }
.icon-btn {
  @apply rounded-full bg-black/50 p-1.5 text-white/80 hover:text-white transition;
  &.danger { @apply hover:text-red-400; }
}
</style>
